import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import {
  listGlobalSections, createGlobalSection, archiveGlobalSection, globalSectionUsage,
  type CmsScope, type GlobalSection, type SectionType, type SectionUsage,
} from '../../lib/cmsAdmin';
import { listOrganizations, type Organization } from '../../lib/tenancy';
import { ApiError } from '../../lib/apiClient';
import { usePageChrome } from '../../admin/pageChrome';
import { useAdminScope } from '../../admin/scope';
import './AdminPages.css';
import './AdminCms.css';

const TYPE_LABEL: Record<SectionType, string> = {
  footer: 'Footer',
  announcement: 'Announcement',
  sponsor_strip: 'Sponsor strip',
};

/**
 * Global sections: content that many pages share.
 *
 * ── A SECTION IS NAMED, NEVER COPIED ─────────────────────────────────────────────────
 *
 * A template referencing a footer holds its ID. Publishing the footer changes every page
 * that names it, at once, with no per-page write. That is the whole reason this screen
 * exists rather than a "duplicate this footer into each template" button, and it is why
 * the Used by column matters more here than anywhere else in the console: before editing
 * a section, somebody should be able to see how far the change reaches.
 */
export function AdminCmsGlobalSections() {
  const { organizationId } = useAdminScope();
  const [sections, setSections] = useState<GlobalSection[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [sectionType, setSectionType] = useState<SectionType | ''>('');
  const [state, setState] = useState<'' | 'published' | 'draft' | 'unpublished'>('');
  const [reload, setReload] = useState(0);
  const [busyId, setBusyId] = useState<string | null>(null);

  usePageChrome({
    title: 'Global sections',
    subtitle: 'Footers, announcements and sponsor strips that many pages share. '
      + 'Publishing one changes every page that references it.',
  });

  useEffect(() => {
    setSections(null);
    listGlobalSections({ organizationId, sectionType: sectionType || null })
      .then(setSections)
      .catch((err: unknown) => setError(err instanceof ApiError
        ? err.message : 'Global sections could not be loaded.'));
  }, [organizationId, sectionType, reload]);

  const shown = useMemo(() => {
    const term = search.trim().toLowerCase();
    return (sections ?? []).filter((s) => {
      if (state === 'published' && s.publishedVersion == null) return false;
      if (state === 'unpublished' && s.publishedVersion != null) return false;
      if (state === 'draft' && s.draftVersion == null) return false;
      if (!term) return true;
      return s.name.toLowerCase().includes(term)
        || (s.description ?? '').toLowerCase().includes(term);
    });
  }, [sections, search, state]);

  async function archive(section: GlobalSection) {
    if (!window.confirm(
      `Archive "${section.name}"? The server refuses this while any published template or `
      + 'page still references it, so nothing can be left pointing at a dead section.',
    )) return;
    setBusyId(section.id);
    try {
      await archiveGlobalSection(section.id);
      setReload((n) => n + 1);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That section could not be archived.');
    } finally { setBusyId(null); }
  }

  return (
    <div className="adm-page">
      {error && <div className="alert alert--error" role="alert">{error}</div>}

      <div className="adm-filters">
        <input className="adm-select" type="search" placeholder="Search sections…"
          value={search} onChange={(e) => setSearch(e.target.value)}
          aria-label="Search global sections" />
        <select className="adm-select" value={sectionType} aria-label="Filter by type"
          onChange={(e) => setSectionType(e.target.value as SectionType | '')}>
          <option value="">All types</option>
          {(Object.keys(TYPE_LABEL) as SectionType[]).map((t) => (
            <option key={t} value={t}>{TYPE_LABEL[t]}</option>
          ))}
        </select>
        <select className="adm-select" value={state} aria-label="Filter by state"
          onChange={(e) => setState(e.target.value as typeof state)}>
          <option value="">Any state</option>
          <option value="published">Published</option>
          <option value="draft">Has a draft</option>
          <option value="unpublished">Never published</option>
        </select>
        <Link className="button button--primary" to="/admin/content/global-sections/new">
          New section
        </Link>
      </div>

      {!sections && !error && <p className="adm-lede">Loading global sections…</p>}
      {sections && shown.length === 0 && (
        <p className="adm-lede">
          No global sections {search || sectionType || state ? 'match that filter' : 'yet'}.
        </p>
      )}

      {shown.length > 0 && (
        <table className="adm-table">
          <thead>
            <tr>
              <th>Name</th><th>Type</th><th>Scope</th><th>Organization</th>
              <th>Live</th><th>Draft</th><th>Used by</th><th>Updated</th><th />
            </tr>
          </thead>
          <tbody>
            {shown.map((s) => (
              <tr key={s.id}>
                <td>
                  <Link to={`/admin/content/global-sections/${s.id}`}>{s.name}</Link>
                  {s.description && <div className="adm-muted">{s.description}</div>}
                </td>
                <td>{TYPE_LABEL[s.sectionType]}</td>
                <td>{s.scope === 'platform' ? 'Platform' : 'Organization'}</td>
                <td>{s.organizationName ?? <span className="adm-muted">—</span>}</td>
                <td>
                  {s.publishedVersion
                    ? `v${s.publishedVersion}`
                    : <span className="adm-muted">never</span>}
                </td>
                <td>
                  {s.draftVersion
                    ? <span className="badge badge--pending">v{s.draftVersion}</span>
                    : '—'}
                </td>
                <td><UsedBy section={s} /></td>
                <td className="adm-nowrap">{new Date(s.updatedAt).toLocaleDateString()}</td>
                <td className="adm-actions">
                  <button type="button" className="button button--ghost adm-btn-sm"
                    disabled={busyId === s.id} onClick={() => { void archive(s); }}>
                    Archive
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

/**
 * How far a change to this section reaches.
 *
 * Fetched per row because it is a live count over templates, pages and events rather
 * than a column anybody maintains. A list that reported a cached number would be
 * reassuring and wrong at exactly the moment it mattered.
 */
function UsedBy({ section }: { section: GlobalSection }) {
  const [usage, setUsage] = useState<SectionUsage | null>(null);

  useEffect(() => {
    let cancelled = false;
    globalSectionUsage(section.id)
      .then((u) => { if (!cancelled) setUsage(u); })
      .catch(() => { /* The list is still useful without the count. */ });
    return () => { cancelled = true; };
  }, [section.id]);

  if (!usage) return <span className="adm-muted">…</span>;
  const referenced = usage.templates + usage.pages;
  if (referenced === 0 && usage.drafts === 0) {
    return <span className="adm-muted">nothing yet</span>;
  }
  return (
    <span title={`${usage.templates} template(s), ${usage.pages} page(s), `
      + `${usage.drafts} draft(s). Affects ${usage.events} live event(s).`}>
      {referenced > 0 && `${referenced} published`}
      {referenced > 0 && usage.events > 0 && ` · ${usage.events} events`}
      {referenced === 0 && usage.drafts > 0 && `${usage.drafts} draft only`}
    </span>
  );
}

/** Create a section. The content comes later, in the editor. */
export function AdminCmsGlobalSectionNew() {
  const navigate = useNavigate();
  const { organizationId } = useAdminScope();

  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [sectionType, setSectionType] = useState<SectionType>('footer');
  const [scope, setScope] = useState<CmsScope>(organizationId ? 'organization' : 'platform');
  const [org, setOrg] = useState(organizationId ?? '');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  usePageChrome({
    title: 'New global section',
    breadcrumb: [
      { label: 'Content & design' },
      { label: 'Global sections', to: '/admin/content/global-sections' },
      { label: 'New' },
    ],
  });

  useEffect(() => { listOrganizations().then(setOrgs).catch(() => setOrgs([])); }, []);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const created = await createGlobalSection({
        sectionType,
        scope,
        organizationId: scope === 'organization' ? (org || null) : null,
        name: name.trim(),
        description: description.trim() || null,
      });
      navigate(`/admin/content/global-sections/${created.id}`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'The section could not be created.');
      setBusy(false);
    }
  }

  return (
    <div className="adm-page">
      <form className="adm-form cms-form" onSubmit={submit}>
        {error && <div className="alert alert--error" role="alert">{error}</div>}

        <label className="field">
          <span className="field__label">Type</span>
          <select className="field__input" value={sectionType}
            onChange={(e) => setSectionType(e.target.value as SectionType)}>
            <option value="footer">Footer</option>
            <option value="announcement">Announcement</option>
            <option value="sponsor_strip">Sponsor strip</option>
          </select>
          <span className="field__help">
            There is no header type. The site header carries sign-in and navigation, which
            belong to the application rather than to editable content.
          </span>
        </label>

        <label className="field">
          <span className="field__label">Name</span>
          <input className="field__input" value={name} required maxLength={120}
            placeholder="Main footer" onChange={(e) => setName(e.target.value)} />
          <span className="field__help">Internal only. Visitors never see it.</span>
        </label>

        <label className="field">
          <span className="field__label">Description</span>
          <input className="field__input" value={description} maxLength={240}
            placeholder="Where this is meant to be used"
            onChange={(e) => setDescription(e.target.value)} />
        </label>

        <label className="field">
          <span className="field__label">Owned by</span>
          <select className="field__input" value={scope}
            onChange={(e) => setScope(e.target.value as CmsScope)}>
            <option value="organization">An organization</option>
            <option value="platform">The platform</option>
          </select>
          <span className="field__help">
            A platform section can be referenced by any organization. An organization&rsquo;s
            section can only be referenced by that organization.
          </span>
        </label>

        {scope === 'organization' && (
          <label className="field">
            <span className="field__label">Organization</span>
            <select className="field__input" value={org} required
              onChange={(e) => setOrg(e.target.value)}>
              <option value="">Choose one…</option>
              {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          </label>
        )}

        <div className="adm-actions">
          <button type="submit" className="button button--primary" disabled={busy || !name.trim()}>
            {busy ? 'Creating…' : 'Create and edit'}
          </button>
          <Link className="button button--ghost" to="/admin/content/global-sections">Cancel</Link>
        </div>
      </form>
    </div>
  );
}
